/**
 * ApprovalActions — the actionable panel on a process's Approvals tab. Lets the
 * user record a tech_lead / storekeeper decision (Approve or Reject-with-reason),
 * or Cancel a draft / pending process. Wired to the real warehouse mutations —
 * the user performs the live action, so we surface an inline "hits live data" note.
 */
import { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { useProcessActions } from '@/api/capacities';
import type { ApproverRole, ProcessApproval, ProcessStatus, ProcessType } from '@/api/capacities';
import { APPROVER_ROLE_LABEL } from '../process-format';

/** The two approval levels, in order. */
const ROLES: ApproverRole[] = ['tech_lead', 'storekeeper'];

/**
 * Backend approve/reject `action` codes are `CONFIRM-{prefix}` / `REJECT-{prefix}`
 * where the prefix is the legacy voucher prefix for the process type
 * (PN nhập · PX xuất · PDC điều chuyển · PKK kiểm kê). Matches legacy exactly.
 */
function actionPrefix(type: ProcessType): string {
  switch (type) {
    case 'export_sale':
    case 'export_loan':
    case 'export_delivery':
      return 'PX';
    case 'internal_transfer':
      return 'PDC';
    case 'inventory_check':
      return 'PKK';
    default: // import_purchase / import_return
      return 'PN';
  }
}

/** Statuses where an approval decision or cancel is still meaningful. */
const ACTIONABLE: ProcessStatus[] = ['draft', 'pending_approval'];

function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })
    ?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong. Please try again.';
}

/** A recorded approval counts as done unless its action explicitly rejects. */
export const isRejection = (action?: string): boolean => /reject|denied|từ chối/i.test(action ?? '');

/** Roles that have already approved — used to pre-select the next pending level. */
function approvedRoles(approvals: ProcessApproval[]): Set<ApproverRole> {
  const done = new Set<ApproverRole>();
  for (const a of approvals) {
    if (a.approver_role && !isRejection(a.action)) done.add(a.approver_role);
  }
  return done;
}

export function ApprovalActions({
  processId,
  type,
  status,
  approvals,
}: {
  processId: string;
  type: ProcessType;
  status: ProcessStatus;
  approvals: ProcessApproval[];
}) {
  const { approve, setStatus } = useProcessActions();
  const done = approvedRoles(approvals);
  const nextRole = ROLES.find((r) => !done.has(r)) ?? 'tech_lead';

  const [role, setRole] = useState<ApproverRole>(nextRole);
  const [comments, setComments] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);

  if (!ACTIONABLE.includes(status)) return null;

  const busy = approve.isPending || setStatus.isPending;
  const prefix = actionPrefix(type);
  // Approve/reject only apply once submitted for approval; a draft can only be
  // cancelled (legacy `canApprove === pending_approval`).
  const canDecide = status === 'pending_approval';

  const submitApprove = () =>
    approve.mutate(
      { id: processId, body: { role, action: `CONFIRM-${prefix}`, comments: comments.trim() } },
      { onSuccess: () => setComments('') },
    );

  const submitReject = () =>
    approve.mutate(
      { id: processId, body: { role, action: `REJECT-${prefix}`, comments: comments.trim() } },
      {
        onSuccess: () => {
          setComments('');
          setRejectOpen(false);
        },
      },
    );

  const submitCancel = () =>
    setStatus.mutate({ id: processId, body: { status: 'cancelled' } });

  return (
    <Card flat>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
        <span style={{ fontSize: 'var(--vn-font-size-md)', fontWeight: 'var(--vn-font-weight-semibold)' }}>
          {canDecide ? 'Record a decision' : 'Process actions'}
        </span>
        <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {canDecide
            ? 'Approve or reject this process on behalf of an approval level.'
            : 'This draft has not been submitted for approval yet.'}
        </span>
      </div>

      <Alert tone="warning" title="Live action">
        Submitting here writes to the live warehouse service — approvals and cancellations are recorded immediately.
      </Alert>

      {approve.isError ? <Alert tone="danger" title="Could not record decision">{errorMessage(approve.error)}</Alert> : null}
      {setStatus.isError ? <Alert tone="danger" title="Could not cancel process">{errorMessage(setStatus.error)}</Alert> : null}

      {canDecide ? (
        <>
          <Field label="Approval level">
            {(p) => (
              <Select value={role} onValueChange={(v) => setRole(v as ApproverRole)}>
                <SelectTrigger {...p}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r} disabled={done.has(r)}>
                      {APPROVER_ROLE_LABEL[r]}
                      {done.has(r) ? ' (approved)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          <Field label="Comments">
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Optional note for this decision…"
              />
            )}
          </Field>
        </>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
        {canDecide ? (
          <>
            <Button variant="primary" loading={approve.isPending} disabled={busy} onClick={submitApprove}>
              Approve
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => setRejectOpen(true)}>
              Reject
            </Button>
          </>
        ) : null}
        <Button
          variant="ghost"
          loading={setStatus.isPending}
          disabled={busy}
          onClick={submitCancel}
          style={{ marginLeft: 'auto' }}
        >
          Cancel process
        </Button>
      </div>

      <ConfirmDialog
        open={rejectOpen}
        onOpenChange={(next) => !busy && setRejectOpen(next)}
        title="Reject this process?"
        description="The process will be sent back. Add a reason so the requester knows what to fix."
        destructive
        confirmLabel="Reject process"
        loading={approve.isPending}
        onConfirm={submitReject}
      >
        <Field label="Reason" required>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Why is this being rejected?"
            />
          )}
        </Field>
      </ConfirmDialog>
      </CardBody>
    </Card>
  );
}
